import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../helpers/api";
import type {
  ConversationsResponse,
  ConversationMessagesResponse,
} from "../types/conversation";

/**
 * Hook for fetching conversation history
 *
 * Uses TanStack Query for caching and automatic refetching.
 * Conversations are sorted by most recent activity on the backend.
 *
 * @returns Query result with conversations list, loading state, and refetch function
 */
export function useConversationHistory() {
  const queryClient = useQueryClient();

  // Fetch conversations list
  const conversationsQuery = useQuery<ConversationsResponse>({
    queryKey: ["conversations"],
    queryFn: () => api.conversations.list(),
    staleTime: 30000, // Consider data fresh for 30 seconds
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });

  const deleteConversation = useMutation({
    mutationFn: (conversationId: string) =>
      api.conversations.delete(conversationId),
    onMutate: async (conversationId) => {
      await queryClient.cancelQueries({ queryKey: ["conversations"] });
      const previous = queryClient.getQueryData<ConversationsResponse>(["conversations"]);
      queryClient.setQueryData<ConversationsResponse>(["conversations"], (old) =>
        old ? { ...old, conversations: old.conversations.filter((c) => c.id !== conversationId) } : old
      );
      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous) queryClient.setQueryData(["conversations"], context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
  });

  return {
    conversations: conversationsQuery.data?.conversations || [],
    isLoading: conversationsQuery.isLoading,
    isError: conversationsQuery.isError,
    error: conversationsQuery.error,
    refetch: conversationsQuery.refetch,
    deleteConversation: deleteConversation.mutateAsync,
    isDeleting: deleteConversation.isLoading,
  };
}

/**
 * Hook for fetching pricing agent conversation history from the server.
 */
export function usePricingConversationHistory() {
  const queryClient = useQueryClient();

  const conversationsQuery = useQuery({
    queryKey: ["pricing-agent-conversations"],
    queryFn: () => api.pricingConfigAgent.listConversations({ limit: 20 }),
    staleTime: 30000,
    refetchOnWindowFocus: true,
  });

  const deleteConversation = useMutation({
    mutationFn: (chatId: string) => api.pricingConfigAgent.delete(chatId),
    onMutate: async (chatId) => {
      await queryClient.cancelQueries({ queryKey: ["pricing-agent-conversations"] });
      const previous = queryClient.getQueryData(["pricing-agent-conversations"]);
      queryClient.setQueryData(["pricing-agent-conversations"], (old: any) =>
        old ? { ...old, conversations: old.conversations.filter((c: any) => c.chatId !== chatId) } : old
      );
      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous) queryClient.setQueryData(["pricing-agent-conversations"], context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["pricing-agent-conversations"] });
    },
  });

  return {
    conversations: conversationsQuery.data?.conversations || [],
    isLoading: conversationsQuery.isLoading,
    refetch: conversationsQuery.refetch,
    deleteConversation: deleteConversation.mutateAsync,
  };
}

/**
 * Hook for fetching messages for a specific conversation
 *
 * @param conversationId - The conversation ID to fetch messages for
 * @param enabled - Whether to fetch messages (default: true)
 * @returns Query result with messages, loading state, and refetch function
 */
export function useConversationMessages(
  conversationId: string | null,
  enabled: boolean = true
) {
  return useQuery<ConversationMessagesResponse>({
    queryKey: ["conversation-messages", conversationId],
    queryFn: () => {
      if (!conversationId) {
        throw new Error("Conversation ID is required");
      }
      return api.conversations.getMessages(conversationId);
    },
    enabled: enabled && !!conversationId,
    staleTime: 60000, // Messages are more stable, cache for 1 minute
  });
}
